// ShopPage.jsx — updated with Cart / Wishlist / Account toolbar + hero slideshow
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import { AuthModal } from "./AuthModal";
import { useAuthContext } from "../hooks/AuthContext";
import config from "../config";

const { API_BASE, UPLOADS_URL, SHOP_UPLOADS_URL } = config;

// If your config.js exports a dedicated SHOP_SLIDESHOW_UPLOADS_URL, this will
// use it automatically. Otherwise it falls back to `${UPLOADS_URL}/shop-slideshow`,
// which matches the \uploads\shop-slideshow directory mentioned.
const SHOP_SLIDESHOW_UPLOADS_URL = config.SHOP_SLIDESHOW_UPLOADS_URL || `${UPLOADS_URL}/shop-slideshow`;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function apiFetch(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function formatPrice(val) {
  if (val === null || val === undefined) return null;
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Product option unit types (BTU, KG gas cylinders, M aluminium tubes,
   KW heat pumps, M2 coolers) — keep in sync with admin/ShopProducts.php ── */
const UNIT_LABELS = { BTU: "BTU", KG: "KG", M: "M", KW: "KW", M2: "M²" };

function formatOptionValue(unitType, value) {
  const label = UNIT_LABELS[unitType] || unitType;
  const num = unitType === "BTU"
    ? Math.round(Number(value)).toLocaleString()
    : String(parseFloat(Number(value).toFixed(2)));
  return `${num} ${label}`;
}

/* If every option on a product shares the same unit type, return its label
   (e.g. "KG") so we can say "Multiple KG sizes" / "Select KG Size".
   Returns null when a product mixes unit types, so callers can fall back
   to a generic "sizes" / "Size" phrasing. */
function optionsUnitLabel(options) {
  if (!options || !options.length) return null;
  const types = [...new Set(options.map((o) => o.unit_type))];
  return types.length === 1 ? (UNIT_LABELS[types[0]] || types[0]) : null;
}

/* ── Build WhatsApp enquiry link ── */
function buildWhatsAppLink(phoneNumber, productName, productSlug) {
  if (!phoneNumber) return null;
  const cleaned = phoneNumber.replace(/\D/g, "");
  const wa = cleaned.startsWith("0") ? "27" + cleaned.slice(1) : cleaned;
  const productUrl = `${window.location.origin}/shop/${productSlug}`;
  const msg = `Hi, I'm interested in the *${productName}* and would like to enquire about pricing and availability.\n\nProduct link: ${productUrl}`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
}

/* ── Star rating ── */
function Stars({ rating }) {
  const r = Math.round(Number(rating) * 2) / 2;
  return (
    <span className="sh-stars" aria-label={`${r} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width="12" height="12" viewBox="0 0 20 20" fill={r >= n ? "#f59e0b" : r >= n - 0.5 ? "url(#half)" : "#e5e7eb"} aria-hidden="true">
          {n === 1 && (
            <defs>
              <linearGradient id="half">
                <stop offset="50%" stopColor="#f59e0b" />
                <stop offset="50%" stopColor="#e5e7eb" />
              </linearGradient>
            </defs>
          )}
          <path d="M10 1.5l2.6 5.3 5.8.85-4.2 4.1 1 5.8L10 14.77l-5.2 2.73 1-5.8-4.2-4.1 5.8-.85L10 1.5z" />
        </svg>
      ))}
    </span>
  );
}

/* ── Product card ── */
function ProductCard({ product, wishlist, onWishlistToggle, onAddToCart, waPhone }) {
  const navigate = useNavigate();
  const inWishlist = wishlist.includes(product.id);
  const options = product.options || [];
  const hasOptions = options.length > 0;
  const unitLabel = optionsUnitLabel(options);

  /* ── Stock: when a product has variant options, its own `quantity` field is
     not used any more — total stock is the sum of stock across all options. ── */
  const totalStock = hasOptions
    ? options.reduce((sum, o) => sum + (Number(o.quantity) || 0), 0)
    : Number(product.quantity) || 0;

  const isOutOfStock = totalStock === 0;
  const isLowStock = totalStock > 0 && totalStock <= (product.low_stock_alert || 5);

  /* ── Price: for variant products, show the low–high range across all options
     (using each option's sale price if it has one). For simple products,
     keep the existing single price / sale price display. ── */
  const hasDiscount = product.sale_price && Number(product.sale_price) < Number(product.price);

  let priceNode;
  if (hasOptions) {
    const effectivePrices = options.map((o) =>
      o.sale_price !== null && o.sale_price !== undefined && Number(o.sale_price) < Number(o.price)
        ? Number(o.sale_price)
        : Number(o.price)
    );
    const minPrice = Math.min(...effectivePrices);
    const maxPrice = Math.max(...effectivePrices);
    priceNode = minPrice === maxPrice ? (
      <span className="sh-card__price">{formatPrice(minPrice)}</span>
    ) : (
      <span className="sh-card__price">{formatPrice(minPrice)} to {formatPrice(maxPrice)}</span>
    );
  } else if (hasDiscount) {
    priceNode = (
      <>
        <span className="sh-card__price sh-card__price--sale">{formatPrice(product.sale_price)}</span>
        <span className="sh-card__price sh-card__price--orig">{formatPrice(product.price)}</span>
      </>
    );
  } else {
    priceNode = <span className="sh-card__price">{formatPrice(product.price) ?? "Contact us"}</span>;
  }

  const img = product.image1;
  const waLink = waPhone ? buildWhatsAppLink(waPhone, product.name, product.slug) : null;

  return (
    <div className="sh-card" onClick={() => navigate(`/shop/${product.slug}`)}>
      <div className="sh-card__media">
        {img ? (
          <img src={`${SHOP_UPLOADS_URL}/${img}`} alt={product.name} className="sh-card__img" loading="lazy" />
        ) : (
          <div className="sh-card__img-fallback">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9l4-4 4 4 4-5 4 5" />
            </svg>
          </div>
        )}
        <div className="sh-card__badges">
          {product.is_featured && <span className="sh-badge sh-badge--featured">Featured</span>}
          {!hasOptions && hasDiscount && <span className="sh-badge sh-badge--sale">−{product.discount_percent}%</span>}
          {isOutOfStock && <span className="sh-badge sh-badge--out">Out of stock</span>}
          {isLowStock && <span className="sh-badge sh-badge--low">Low stock</span>}
        </div>
        <button
          className={`sh-card__wish ${inWishlist ? "sh-card__wish--active" : ""}`}
          onClick={(e) => { e.stopPropagation(); onWishlistToggle(product.id); }}
          aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill={inWishlist ? "#ef4444" : "none"} stroke={inWishlist ? "#ef4444" : "currentColor"} strokeWidth="2" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
      </div>
      <div className="sh-card__body">
        {product.brand && <p className="sh-card__brand">{product.brand}</p>}
        <h3 className="sh-card__name">{product.name}</h3>
        {product.energy_rating && (
          <p className="sh-card__spec">Energy: <strong>{product.energy_rating}</strong>
            {product.capacity_kw && <> · {product.capacity_kw} kW</>}
            {product.coverage_area_m2 && <> · {product.coverage_area_m2} m²</>}
          </p>
        )}
        <div className="sh-card__price-row">
          {priceNode}
        </div>

        {/* ── VAT / options note as a tag/pill instead of plain text ── */}
        <div className="sh-card__tags">
          <span className="sh-tag sh-tag--vat">Excl. VAT</span>
          {hasOptions && (
            <span className="sh-tag sh-tag--btu">
              {unitLabel ? `Multiple ${unitLabel} sizes` : `${options.length} sizes available`}
            </span>
          )}
        </div>

        {/* ── Split CTA row: Add to Cart / Select Size + WhatsApp icon ── */}
        <div className="sh-card__cta-row" onClick={(e) => e.stopPropagation()}>
          {hasOptions ? (
            <button
              className="sh-card__cta"
              disabled={isOutOfStock}
              onClick={(e) => { e.stopPropagation(); navigate(`/shop/${product.slug}`); }}
              aria-label={`Choose a size for ${product.name}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {isOutOfStock ? "Out of stock" : (unitLabel ? `Select ${unitLabel} Size` : "Select Size")}
            </button>
          ) : (
            <button
              className="sh-card__cta"
              disabled={isOutOfStock}
              onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
              aria-label={`Add ${product.name} to cart`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {isOutOfStock ? "Out of stock" : "Add to cart"}
            </button>
          )}

          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="sh-card__wa-btn"
              aria-label={`Enquire about ${product.name} on WhatsApp`}
              title="Enquire on WhatsApp"
              onClick={(e) => e.stopPropagation()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.928a.5.5 0 0 0 .6.633l6.313-1.434A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.001-1.369l-.358-.214-3.715.844.877-3.609-.234-.371A9.818 9.818 0 1 1 12 21.818z"/>
              </svg>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Filter sidebar ── */
function FilterSidebar({ products, filters, onChange, onClear }) {
  const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))].sort(), [products]);
  const types = useMemo(() => [...new Set(products.map((p) => p.product_type).filter(Boolean))].sort(), [products]);
  const ratings = useMemo(() => [...new Set(products.map((p) => p.energy_rating).filter(Boolean))].sort(), [products]);
  const hasActive = Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : v !== ""));

  function toggleArr(key, val) {
    const arr = filters[key] || [];
    onChange(key, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  }

  function CheckGroup({ label, options, filterKey }) {
    return options.length === 0 ? null : (
      <div className="sh-filter__group">
        <p className="sh-filter__label">{label}</p>
        {options.map((opt) => (
          <label key={opt} className="sh-filter__check">
            <input
              type="checkbox"
              checked={(filters[filterKey] || []).includes(opt)}
              onChange={() => toggleArr(filterKey, opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <aside className="sh-filters">
      <div className="sh-filter__header">
        <span className="sh-filter__title">Filters</span>
        {hasActive && <button className="sh-filter__clear" onClick={onClear}>Clear all</button>}
      </div>
      <div className="sh-filter__group">
        <p className="sh-filter__label">Price range</p>
        <div className="sh-filter__price-row">
          <input type="number" placeholder="Min" min="0" value={filters.priceMin}
            onChange={(e) => onChange("priceMin", e.target.value)} className="sh-filter__input" />
          <span className="sh-filter__price-sep">–</span>
          <input type="number" placeholder="Max" min="0" value={filters.priceMax}
            onChange={(e) => onChange("priceMax", e.target.value)} className="sh-filter__input" />
        </div>
      </div>
      <CheckGroup label="Brand" options={brands} filterKey="brands" />
      <CheckGroup label="Type" options={types} filterKey="types" />
      <CheckGroup label="Energy rating" options={ratings} filterKey="energyRatings" />
      <div className="sh-filter__group">
        <p className="sh-filter__label">Availability</p>
        <label className="sh-filter__check">
          <input type="checkbox" checked={filters.inStockOnly} onChange={(e) => onChange("inStockOnly", e.target.checked)} />
          <span>In stock only</span>
        </label>
      </div>
      <div className="sh-filter__group">
        <p className="sh-filter__label">Deals</p>
        <label className="sh-filter__check">
          <input type="checkbox" checked={filters.onSaleOnly} onChange={(e) => onChange("onSaleOnly", e.target.checked)} />
          <span>On sale</span>
        </label>
        <label className="sh-filter__check">
          <input type="checkbox" checked={filters.featuredOnly} onChange={(e) => onChange("featuredOnly", e.target.checked)} />
          <span>Featured</span>
        </label>
      </div>
    </aside>
  );
}

/* ── Pagination controls ── */
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  function getPageList() {
    const pages = [];
    const windowSize = 1;
    const start = Math.max(1, currentPage - windowSize);
    const end = Math.min(totalPages, currentPage + windowSize);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("ellipsis-start");
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("ellipsis-end");
      pages.push(totalPages);
    }
    return pages;
  }

  return (
    <nav className="sh-pagination" aria-label="Product pages">
      <button
        className="sh-page-btn sh-page-btn--nav"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="sh-page-btn__label">Prev</span>
      </button>

      <div className="sh-page-numbers">
        {getPageList().map((p, idx) =>
          typeof p === "number" ? (
            <button
              key={p}
              className={`sh-page-btn sh-page-btn--num ${p === currentPage ? "sh-page-btn--active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
              aria-label={`Page ${p}`}
            >
              {p}
            </button>
          ) : (
            <span key={p + idx} className="sh-page-ellipsis">…</span>
          )
        )}
      </div>

      <button
        className="sh-page-btn sh-page-btn--nav"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <span className="sh-page-btn__label">Next</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </nav>
  );
}

/* ════════════════════════════════════════════
   HERO SLIDESHOW
   Pulls from GET {API_BASE}/shop-slideshow.php
   Images are 1825×862 — the hero box locks that
   aspect ratio so images are shown in full (object-fit:
   contain, no tinted overlay, no title/subtitle text)
   and scale down cleanly on small screens.
   ════════════════════════════════════════════ */
const SLIDE_INTERVAL_MS = 6000;

function ShopHeroSlideshow({ slides }) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);

  const hasSlides = slides.length > 0;
  const slideCount = slides.length;

  const goTo = useCallback((i) => {
    if (!slideCount) return;
    setIndex(((i % slideCount) + slideCount) % slideCount);
  }, [slideCount]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  /* ── Autoplay — pauses on hover/focus and restarts whenever index changes ── */
  const startAutoplay = useCallback(() => {
    clearInterval(timerRef.current);
    if (slideCount > 1) {
      timerRef.current = setInterval(() => {
        setIndex((i) => (i + 1) % slideCount);
      }, SLIDE_INTERVAL_MS);
    }
  }, [slideCount]);

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(timerRef.current);
  }, [startAutoplay]);

  function pauseAutoplay() { clearInterval(timerRef.current); }
  function resumeAutoplay() { startAutoplay(); }

  /* ── Simple touch-swipe support for mobile ── */
  function handleTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) delta < 0 ? next() : prev();
    touchStartX.current = null;
  }

  if (!hasSlides) return null;

  return (
    <div
      className="sh-hero"
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`sh-hero__slide ${i === index ? "sh-hero__slide--active" : ""}`}
          aria-hidden={i !== index}
        >
          <img
            src={`${SHOP_SLIDESHOW_UPLOADS_URL}/${slide.image_filename}`}
            alt={slide.title || "Shop promotion"}
            className="sh-hero__img"
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      {slideCount > 1 && (
        <>
          <button className="sh-hero__arrow sh-hero__arrow--prev" onClick={prev} aria-label="Previous slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="sh-hero__arrow sh-hero__arrow--next" onClick={next} aria-label="Next slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="sh-hero__dots" role="tablist" aria-label="Slideshow navigation">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                className={`sh-hero__dot ${i === index ? "sh-hero__dot--active" : ""}`}
                onClick={() => goTo(i)}
                role="tab"
                aria-selected={i === index}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN ShopPage
   Props:
     cartItems      — array of { ...product, qty }
     onAddToCart    — fn(product)
     customer       — null | { id, first_name, last_name, email, … }
     isLoggedIn     — bool
     onAuthLogin    — fn(token, customer) — called after successful auth
     onLogout       — fn()
     onOpenCart     — fn() — opens cart drawer / page
     onOpenWishlist — fn() — opens wishlist page (optional)
   ════════════════════════════════════════════ */
function ShopPage({ cartItems, onAddToCart, customer, isLoggedIn, onAuthLogin, onLogout, onOpenCart, onOpenWishlist, onOpenAuth }) {
  const navigate = useNavigate();
  const { token } = useAuthContext();

  const PRODUCTS_PER_PAGE = 20;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [contact, setContact] = useState(null);
  const [slides, setSlides] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [activeCategory, setActiveCategory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    priceMin: "", priceMax: "",
    brands: [], types: [], energyRatings: [],
    inStockOnly: false, onSaleOnly: false, featuredOnly: false,
  });

  /* ── User dropdown ── */
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchJSON(`${API_BASE}/shop-products.php`),
      fetchJSON(`${API_BASE}/shop-categories.php`),
    ]).then(([prods, cats]) => {
      if (cancelled) return;
      setProducts(Array.isArray(prods) ? prods : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setError("Failed to load products.");
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  /* ── Fetch company contact for WhatsApp ── */
  useEffect(() => {
    fetchJSON(`${API_BASE}/company-contact.php`)
      .then((data) => setContact(data || null))
      .catch(() => {});
  }, []);

  /* ── Fetch hero slideshow slides ── */
  useEffect(() => {
    fetchJSON(`${API_BASE}/shop-slideshow.php`)
      .then((data) => setSlides(Array.isArray(data) ? data : []))
      .catch(() => setSlides([]));
  }, []);

  /* ── Load wishlist IDs from server when logged in ── */
  useEffect(() => {
    if (!isLoggedIn || !token) { setWishlist([]); return; }
    apiFetch(`${API_BASE}/customer-wishlist.php`, token)
      .then((data) => setWishlist((data.wishlist || []).map((p) => p.id)))
      .catch(() => setWishlist([]));
  }, [isLoggedIn, token]);

  const handleFilterChange = useCallback((key, val) => setFilters((f) => ({ ...f, [key]: val })), []);
  const clearFilters = useCallback(() => setFilters({
    priceMin: "", priceMax: "", brands: [], types: [], energyRatings: [],
    inStockOnly: false, onSaleOnly: false, featuredOnly: false,
  }), []);

  /* ── Toggle wishlist via server (customer-wishlist.php POST toggles add/remove) ── */
  const handleWishlistToggle = useCallback((id) => {
    if (!isLoggedIn || !token) {
      onOpenAuth?.("login");
      return;
    }
    apiFetch(`${API_BASE}/customer-wishlist.php`, token, {
      method: "POST",
      body: JSON.stringify({ product_id: id }),
    })
      .then((data) => {
        setWishlist((prev) =>
          data.action === "added" ? [...prev, id] : prev.filter((x) => x !== id)
        );
      })
      .catch((e) => alert("Could not update wishlist: " + e.message));
  }, [isLoggedIn, token, onOpenAuth]);

  const handleAddToCart = useCallback((product) => {
    onAddToCart(product);
  }, [onAddToCart]);

  const visibleProducts = useMemo(() => {
    let list = [...products];
    if (activeCategory !== "all") list = list.filter((p) => String(p.category_id) === String(activeCategory));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q)
      );
    }
    if (filters.priceMin !== "") list = list.filter((p) => Number(p.sale_price || p.price) >= Number(filters.priceMin));
    if (filters.priceMax !== "") list = list.filter((p) => Number(p.sale_price || p.price) <= Number(filters.priceMax));
    if (filters.brands.length) list = list.filter((p) => filters.brands.includes(p.brand));
    if (filters.types.length) list = list.filter((p) => filters.types.includes(p.product_type));
    if (filters.energyRatings.length) list = list.filter((p) => filters.energyRatings.includes(p.energy_rating));
    if (filters.inStockOnly) list = list.filter((p) => {
      const opts = p.options || [];
      const stock = opts.length ? opts.reduce((s, o) => s + (Number(o.quantity) || 0), 0) : p.quantity;
      return stock > 0;
    });
    if (filters.onSaleOnly) list = list.filter((p) => p.sale_price && Number(p.sale_price) < Number(p.price));
    if (filters.featuredOnly) list = list.filter((p) => p.is_featured);
    if (sortBy === "price-asc") list.sort((a, b) => Number(a.sale_price || a.price) - Number(b.sale_price || b.price));
    else if (sortBy === "price-desc") list.sort((a, b) => Number(b.sale_price || b.price) - Number(a.sale_price || a.price));
    else if (sortBy === "name-asc") list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "newest") list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [products, activeCategory, search, filters, sortBy]);

  /* ── Pagination: reset to page 1 whenever the filtered/sorted list changes ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, search, filters, sortBy]);

  const totalPages = Math.max(1, Math.ceil(visibleProducts.length / PRODUCTS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginatedProducts = useMemo(() => {
    const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return visibleProducts.slice(startIdx, startIdx + PRODUCTS_PER_PAGE);
  }, [visibleProducts, currentPage]);

  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [totalPages]);

  const cartCount = cartItems?.reduce((s, i) => s + i.qty, 0) ?? 0;
  const wishlistCount = wishlist.length;

  return (
    <>
      <div className="sh">
        {/* ── HERO SLIDESHOW ── */}
        {slides.length > 0 ? (
          <ShopHeroSlideshow slides={slides} />
        ) : (
          <div className="sh-hero">
            <img src={`${UPLOADS_URL}/hero/hero-shop.png`} alt="" className="sh-hero__img sh-hero__img--static" />
          </div>
        )}

        {/* ── TOOLBAR ── */}
        <div className="sh-toolbar-wrap">
          <div className="sh-toolbar">
            {/* Filter toggle — mobile only */}
            <button className="sh-filter-toggle" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} aria-label="Toggle filters">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="20" y2="12" /><line x1="12" y1="18" x2="20" y2="18" />
              </svg>
            </button>

            {/* Search */}
            <div className="sh-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="search" placeholder="Search products, brands, SKUs…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="sh-search__input" aria-label="Search products"
              />
              {search && (
                <button className="sh-search__clear" onClick={() => setSearch("")} aria-label="Clear search">
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4l12 12M16 4L4 16" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>

            {/* Sort — desktop */}
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sh-sort sh-sort--desktop" aria-label="Sort products">
              <option value="default">Sort: Default</option>
              <option value="price-asc">Price: Low–High</option>
              <option value="price-desc">Price: High–Low</option>
              <option value="name-asc">Name: A–Z</option>
              <option value="newest">Newest</option>
            </select>

            {/* Action buttons */}
            <div className="sh-actions">
              {/* Wishlist */}
              <button
                className="sh-action-btn"
                onClick={() => onOpenWishlist ? onOpenWishlist() : navigate("/wishlist")}
                aria-label={`Wishlist (${wishlistCount} items)`}
                title="Wishlist"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                <span className="sh-action-btn__label">Wishlist</span>
                {wishlistCount > 0 && <span className="sh-action-btn__badge">{wishlistCount}</span>}
              </button>

              {/* Account */}
              {isLoggedIn ? (
                <div className="sh-user-menu">
                  <button
                    className="sh-action-btn sh-action-btn--account"
                    onClick={() => setUserMenuOpen((v) => !v)}
                    aria-expanded={userMenuOpen}
                    aria-label="Account menu"
                  >
                    <span className="sh-avatar">
                      {customer?.first_name?.[0]?.toUpperCase() || "U"}
                    </span>
                    <span className="sh-action-btn__label">{customer?.first_name || "Account"}</span>
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" className={`sh-chevron ${userMenuOpen ? "sh-chevron--up" : ""}`}>
                      <path d="M4 7l6 6 6-6" strokeLinecap="round" />
                    </svg>
                  </button>
                  {userMenuOpen && (
                    <>
                      <div className="sh-user-backdrop" onClick={() => setUserMenuOpen(false)} />
                      <div className="sh-user-dropdown">
                        <div className="sh-user-dropdown__header">
                          <span className="sh-user-dropdown__name">{customer?.first_name} {customer?.last_name}</span>
                          <span className="sh-user-dropdown__email">{customer?.email}</span>
                        </div>
                        <button className="sh-user-dropdown__item" onClick={() => { setUserMenuOpen(false); navigate("/account"); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                          My Profile
                        </button>
                        <button className="sh-user-dropdown__item" onClick={() => { setUserMenuOpen(false); navigate("/account?tab=orders"); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
                          Order History
                        </button>
                        <div className="sh-user-dropdown__divider" />
                        <button className="sh-user-dropdown__item sh-user-dropdown__item--danger" onClick={() => { setUserMenuOpen(false); onLogout(); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                          Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button className="sh-action-btn" onClick={() => onOpenAuth?.("login")} aria-label="Sign in" title="Account">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                  <span className="sh-action-btn__label">Account</span>
                </button>
              )}

              {/* Cart */}
              <button
                className="sh-action-btn sh-action-btn--cart"
                onClick={() => onOpenCart ? onOpenCart() : navigate("/cart")}
                aria-label={`Cart (${cartCount} items)`}
                title="Cart"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                <span className="sh-action-btn__label">Cart</span>
                {cartCount > 0 && <span className="sh-action-btn__badge sh-action-btn__badge--cart">{cartCount}</span>}
              </button>
            </div>
          </div>

          {/* Sort — mobile (below toolbar) */}
          <div className="sh-toolbar-mobile-sort">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sh-sort sh-sort--mobile" aria-label="Sort products">
              <option value="default">Sort: Default</option>
              <option value="price-asc">Price: Low–High</option>
              <option value="price-desc">Price: High–Low</option>
              <option value="name-asc">Name: A–Z</option>
              <option value="newest">Newest</option>
            </select>
          </div>

          {/* Category tabs */}
          {categories.length > 0 && (
            <div className="sh-cats">
              <button className={`sh-cat ${activeCategory === "all" ? "sh-cat--active" : ""}`} onClick={() => setActiveCategory("all")}>
                All <span className="sh-cat__count">{products.length}</span>
              </button>
              {categories.map((c) => {
                const count = products.filter((p) => String(p.category_id) === String(c.id)).length;
                return count > 0 ? (
                  <button key={c.id} className={`sh-cat ${activeCategory === String(c.id) ? "sh-cat--active" : ""}`} onClick={() => setActiveCategory(String(c.id))}>
                    {c.name} <span className="sh-cat__count">{count}</span>
                  </button>
                ) : null;
              })}
            </div>
          )}
        </div>

        {/* ── BODY ── */}
        <div className="sh-body">
          <div className="sh-body__inner">
            <div className={`sh-sidebar ${showFilters ? "sh-sidebar--open" : ""}`}>
              <FilterSidebar products={products} filters={filters} onChange={handleFilterChange} onClear={clearFilters} />
            </div>
            <div className="sh-main">
              <div className="sh-results-bar">
                <span className="sh-results-count">
                  {loading
                    ? "Loading…"
                    : visibleProducts.length === 0
                    ? "0 products"
                    : `Showing ${(currentPage - 1) * PRODUCTS_PER_PAGE + 1}–${Math.min(currentPage * PRODUCTS_PER_PAGE, visibleProducts.length)} of ${visibleProducts.length} product${visibleProducts.length !== 1 ? "s" : ""}`}
                </span>
              </div>
              {loading && <div className="sh-state">Loading products…</div>}
              {!loading && error && <div className="sh-state sh-state--error">{error}</div>}
              {!loading && !error && visibleProducts.length === 0 && (
                <div className="sh-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                  </svg>
                  <p>No products match your search.</p>
                  <button className="sh-state__btn" onClick={() => { setSearch(""); clearFilters(); setActiveCategory("all"); }}>
                    Clear filters
                  </button>
                </div>
              )}
              {!loading && !error && visibleProducts.length > 0 && (
                <>
                  <div className="sh-grid">
                    {paginatedProducts.map((p) => (
                      <ProductCard
                        key={p.id}
                        product={p}
                        wishlist={wishlist}
                        onWishlistToggle={handleWishlistToggle}
                        onAddToCart={handleAddToCart}
                        waPhone={contact?.phone_number || null}
                      />
                    ))}
                  </div>
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                </>
              )}
            </div>
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');
          .sh *, .sh *::before, .sh *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .sh a { text-decoration: none; }
          .sh { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          /* ── HERO SLIDESHOW ──
             Images are 1825×862 (≈2.12:1). The box locks that aspect ratio
             so the full image is always visible — no cropping, no tint,
             no overlay text. object-fit: contain guarantees nothing is
             cut off even if a slide's own ratio differs slightly. ── */
          .sh-hero {
            position: relative;
            width: 100%;
            aspect-ratio: 1825 / 698;
            min-height: 220px;
            overflow: hidden;
            background: #f0f2f5;
          }
          .sh-hero__slide {
            position: absolute; inset: 0;
            opacity: 0;
            transition: opacity .9s ease;
          }
          .sh-hero__slide--active { opacity: 1; z-index: 1; }
          .sh-hero__img {
            width: 100%; height: 100%;
            object-fit: contain;
            object-position: center;
            display: block;
          }
          .sh-hero__img--static { position: absolute; inset: 0; }

          /* Arrows */
          .sh-hero__arrow {
            position: absolute; top: 50%; transform: translateY(-50%);
            z-index: 4;
            width: 40px; height: 40px;
            display: flex; align-items: center; justify-content: center;
            background: rgba(255,255,255,.85);
            border: 1.5px solid rgba(0,0,0,.08);
            color: #1a2236;
            border-radius: 50%;
            cursor: pointer;
            backdrop-filter: blur(4px);
            box-shadow: 0 2px 10px rgba(0,0,0,.1);
            transition: background .2s, transform .2s;
          }
          .sh-hero__arrow:hover { background: #fff; transform: translateY(-50%) scale(1.06); }
          .sh-hero__arrow--prev { left: 16px; }
          .sh-hero__arrow--next { right: 16px; }

          /* Dots */
          .sh-hero__dots {
            position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
            z-index: 4;
            display: flex; gap: 8px;
          }
          .sh-hero__dot {
            width: 8px; height: 8px; border-radius: 50%;
            background: rgba(1,21,70,.28);
            border: 1px solid rgba(255,255,255,.6);
            cursor: pointer;
            transition: background .2s, transform .2s, width .2s;
          }
          .sh-hero__dot:hover { background: rgba(1,21,70,.5); }
          .sh-hero__dot--active { background: #013d97; width: 22px; border-radius: 4px; }

          /* ── TOOLBAR ── */
          .sh-toolbar-wrap { background: #fff; border-bottom: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(1,21,70,.06); position: sticky; top: 96px; z-index: 100; }
          .sh-toolbar { max-width: 1280px; margin: 0 auto; padding: 10px 40px; display: flex; align-items: center; gap: 10px; }

          /* Search */
          .sh-search { flex: 1; display: flex; align-items: center; gap: 10px; background: #f8f9fb; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 0 12px; min-width: 0; }
          .sh-search svg { color: #8890a0; flex-shrink: 0; }
          .sh-search__input { flex: 1; border: none; background: none; font-family: inherit; font-size: 14px; color: #1a2236; padding: 9px 0; outline: none; min-width: 0; }
          .sh-search__input::placeholder { color: #9ca3af; }
          .sh-search__clear { background: none; border: none; cursor: pointer; color: #8890a0; display: flex; align-items: center; padding: 4px; border-radius: 4px; }
          .sh-search__clear:hover { color: #013d97; }

          /* Sort */
          .sh-sort { border: 1.5px solid #e2e8f0; background: #fff; font-family: inherit; font-size: 13px; color: #1a2236; padding: 8px 12px; border-radius: 8px; cursor: pointer; outline: none; white-space: nowrap; }
          .sh-sort:focus { border-color: #013d97; }
          .sh-sort--mobile { display: none; }

          /* Mobile toolbar sort row */
          .sh-toolbar-mobile-sort { display: none; }

          /* ── ACTION BUTTONS ── */
          .sh-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
          .sh-action-btn {
            position: relative;
            display: flex; align-items: center; gap: 6px;
            padding: 8px 12px;
            border: 1.5px solid #e2e8f0;
            background: #fff;
            border-radius: 8px;
            cursor: pointer;
            font-family: inherit; font-size: 13px; font-weight: 700; color: #374151;
            transition: border-color .18s, background .18s, color .18s;
            white-space: nowrap;
          }
          .sh-action-btn:hover { border-color: #013d97; color: #013d97; background: #f0f5ff; }
          .sh-action-btn--cart { background: #013d97; color: #fff; border-color: #013d97; }
          .sh-action-btn--cart:hover { background: #022d75; border-color: #022d75; color: #fff; }
          .sh-action-btn__badge {
            position: absolute; top: -6px; right: -6px;
            min-width: 18px; height: 18px;
            border-radius: 9px;
            background: #ef4444; color: #fff;
            font-size: 10px; font-weight: 800;
            display: flex; align-items: center; justify-content: center;
            padding: 0 4px;
            border: 2px solid #fff;
          }
          .sh-action-btn__badge--cart { background: #f59e0b; }
          .sh-action-btn__label { font-size: 13px; }

          /* Avatar */
          .sh-avatar {
            width: 22px; height: 22px; border-radius: 50%;
            background: #013d97; color: #fff;
            font-size: 11px; font-weight: 900;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
          .sh-action-btn--account { border-color: #c8d8f5; background: #eef3fb; color: #013d97; }
          .sh-action-btn--account:hover { background: #dce9f9; border-color: #013d97; }

          /* Chevron */
          .sh-chevron { transition: transform .18s; }
          .sh-chevron--up { transform: rotate(180deg); }

          /* User dropdown */
          .sh-user-menu { position: relative; }
          .sh-user-backdrop { position: fixed; inset: 0; z-index: 90; }
          .sh-user-dropdown {
            position: absolute; top: calc(100% + 8px); right: 0;
            min-width: 220px;
            background: #fff;
            border: 1.5px solid #e2e8f0;
            border-radius: 10px;
            box-shadow: 0 8px 24px rgba(1,21,70,.14);
            z-index: 200;
            overflow: hidden;
            animation: sh-drop-in .16s ease;
          }
          @keyframes sh-drop-in {
            from { opacity: 0; transform: translateY(-6px); }
            to   { opacity: 1; transform: none; }
          }
          .sh-user-dropdown__header {
            padding: 14px 16px 12px;
            border-bottom: 1px solid #f0f2f5;
          }
          .sh-user-dropdown__name { display: block; font-size: 14px; font-weight: 800; color: #1a2236; }
          .sh-user-dropdown__email { display: block; font-size: 12px; color: #8890a0; margin-top: 2px; }
          .sh-user-dropdown__item {
            display: flex; align-items: center; gap: 10px;
            width: 100%; padding: 11px 16px;
            background: none; border: none; cursor: pointer;
            font-family: inherit; font-size: 13.5px; font-weight: 600; color: #374151;
            text-align: left; transition: background .15s;
          }
          .sh-user-dropdown__item:hover { background: #f5f7fa; color: #013d97; }
          .sh-user-dropdown__item--danger { color: #991b1b; }
          .sh-user-dropdown__item--danger:hover { background: #fef2f2; color: #991b1b; }
          .sh-user-dropdown__divider { border-top: 1px solid #f0f2f5; }

          /* Mobile filter toggle */
          .sh-filter-toggle {
            display: none;
            align-items: center; justify-content: center;
            width: 38px; height: 38px;
            border: 1.5px solid #e2e8f0; background: #fff;
            border-radius: 8px; cursor: pointer;
            color: #374151; flex-shrink: 0;
          }
          .sh-filter-toggle:hover { border-color: #013d97; color: #013d97; }

          /* ── CATEGORY TABS ── */
          .sh-cats { max-width: 1280px; margin: 0 auto; padding: 0 40px 10px; display: flex; gap: 4px; overflow-x: auto; scrollbar-width: none; }
          .sh-cats::-webkit-scrollbar { display: none; }
          .sh-cat { display: inline-flex; align-items: center; gap: 7px; padding: 7px 16px; background: none; border: 1.5px solid transparent; border-radius: 6px; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #5a6478; white-space: nowrap; transition: color .18s, background .18s, border-color .18s; }
          .sh-cat:hover { color: #013d97; background: #eef3fb; border-color: #c8d8f5; }
          .sh-cat--active { color: #fff; background: #013d97; border-color: #013d97; }
          .sh-cat--active:hover { background: #022d75; border-color: #022d75; }
          .sh-cat__count { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 10px; font-size: 11px; font-weight: 800; background: rgba(1,61,151,.12); color: #013d97; transition: background .18s, color .18s; }
          .sh-cat--active .sh-cat__count { background: rgba(255,255,255,.22); color: #fff; }

          /* ── BODY LAYOUT ── */
          .sh-body { padding: 40px 0 80px; }
          .sh-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; display: grid; grid-template-columns: 260px 1fr; gap: 28px; align-items: start; }

          /* ── FILTER SIDEBAR ── */
          .sh-sidebar { position: sticky; top: 200px; }
          .sh-filters { background: #fff; border-radius: 10px; box-shadow: 0 1px 4px rgba(0,0,0,.06); padding: 20px; }
          .sh-filter__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
          .sh-filter__title { font-size: 14px; font-weight: 800; color: #1a2236; text-transform: uppercase; letter-spacing: .06em; }
          .sh-filter__clear { background: none; border: none; cursor: pointer; font-family: inherit; font-size: 12.5px; font-weight: 700; color: #013d97; padding: 0; }
          .sh-filter__clear:hover { text-decoration: underline; }
          .sh-filter__group { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #f0f2f5; }
          .sh-filter__group:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
          .sh-filter__label { font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #8890a0; margin-bottom: 10px; }
          .sh-filter__check { display: flex; align-items: center; gap: 9px; padding: 4px 0; cursor: pointer; font-size: 13.5px; color: #374151; }
          .sh-filter__check input[type=checkbox] { width: 15px; height: 15px; accent-color: #013d97; cursor: pointer; }
          .sh-filter__price-row { display: flex; align-items: center; gap: 8px; }
          .sh-filter__price-sep { color: #9ca3af; font-size: 13px; }
          .sh-filter__input { flex: 1; border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 7px 10px; font-family: inherit; font-size: 13px; color: #1a2236; outline: none; min-width: 0; }
          .sh-filter__input:focus { border-color: #013d97; }

          /* ── PRODUCT GRID — 4 per row on desktop ── */
          .sh-main { min-width: 0; }
          .sh-results-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
          .sh-results-count { font-size: 13.5px; color: #8890a0; font-weight: 600; }
          .sh-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }

          /* ── STATE ── */
          .sh-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; padding: 64px 24px; text-align: center; background: #fff; border-radius: 10px; color: #8890a0; font-size: 14px; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
          .sh-state svg { color: #c0c8d8; }
          .sh-state--error { color: #c0392b; }
          .sh-state__btn { background: #013d97; color: #fff; border: none; padding: 10px 24px; border-radius: 8px; font-family: inherit; font-size: 13.5px; font-weight: 700; cursor: pointer; }

          /* ── PRODUCT CARD — sized for 4-up grid ── */
          .sh-card { background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 8px rgba(1,21,70,.08); cursor: pointer; display: flex; flex-direction: column; transition: transform .2s, box-shadow .2s; }
          .sh-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(1,21,70,.12); }
          .sh-card__media { position: relative; aspect-ratio: 1/1; overflow: hidden; background: #f0f2f5; }
          .sh-card__img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s ease; }
          .sh-card:hover .sh-card__img { transform: scale(1.04); }
          .sh-card__img-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #e8edf6, #d4ddf0); color: #a0aec0; }
          .sh-card__badges { position: absolute; top: 8px; left: 8px; display: flex; flex-direction: column; gap: 4px; }
          .sh-badge { display: inline-block; padding: 2.5px 7px; border-radius: 4px; font-size: 9.5px; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; }
          .sh-badge--featured { background: #013d97; color: #fff; }
          .sh-badge--sale { background: #ef4444; color: #fff; }
          .sh-badge--out { background: #6b7280; color: #fff; }
          .sh-badge--low { background: #f59e0b; color: #fff; }
          .sh-card__wish { position: absolute; top: 8px; right: 8px; width: 30px; height: 30px; border-radius: 50%; background: rgba(255,255,255,.92); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,.12); transition: background .2s, transform .2s; }
          .sh-card__wish svg { width: 16px; height: 16px; }
          .sh-card__wish:hover { background: #fff; transform: scale(1.12); }
          .sh-card__wish--active { background: #fff1f1; }
          .sh-card__body { padding: 13px; display: flex; flex-direction: column; gap: 5px; flex: 1; }
          .sh-card__brand { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: #029add; }
          .sh-card__name { font-size: 13px; font-weight: 700; color: #1a2236; line-height: 1.32; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
          .sh-card__spec { font-size: 11px; color: #6b7280; }
          .sh-card__spec strong { color: #013d97; }
          .sh-card__price-row { display: flex; align-items: baseline; gap: 6px; margin-top: 3px; flex-wrap: wrap; }
          .sh-card__price { font-size: 14px; font-weight: 800; color: #1a2236; }
          .sh-card__price--sale { color: #013d97; }
          .sh-card__price--orig { font-size: 11.5px; font-weight: 500; color: #9ca3af; text-decoration: line-through; }

          /* ── VAT / options pills ── */
          .sh-card__tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 1px; }
          .sh-tag { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 20px; font-size: 9.5px; font-weight: 700; letter-spacing: .01em; }
          .sh-tag--vat { background: #f0f2f5; color: #6b7280; }
          .sh-tag--btu { background: #eef3fb; color: #013d97; }

          /* ── CARD CTA ROW (Add to Cart + WhatsApp) ── */
          .sh-card__cta-row { margin-top: auto; padding-top: 9px; display: flex; gap: 6px; align-items: stretch; }
          .sh-card__cta { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; background: #013d97; color: #fff; border: none; border-radius: 7px; padding: 9px 8px; font-family: inherit; font-size: 12px; font-weight: 700; cursor: pointer; transition: background .2s; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .sh-card__cta svg { flex-shrink: 0; }
          .sh-card__cta:hover:not(:disabled) { background: #022d75; }
          .sh-card__cta:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }
          .sh-card__wa-btn { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 36px; background: #25d366; color: #fff; border-radius: 7px; text-decoration: none; transition: background .18s, transform .18s; box-shadow: 0 2px 6px rgba(37,211,102,.28); }
          .sh-card__wa-btn:hover { background: #1da851; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(37,211,102,.38); }

          .sh-stars { display: inline-flex; gap: 2px; }

          /* ── PAGINATION ── */
          .sh-pagination { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 32px; flex-wrap: wrap; }
          .sh-page-numbers { display: flex; align-items: center; gap: 6px; }
          .sh-page-btn {
            display: flex; align-items: center; justify-content: center; gap: 6px;
            min-width: 38px; height: 38px; padding: 0 12px;
            border: 1.5px solid #e2e8f0; background: #fff;
            border-radius: 8px; cursor: pointer;
            font-family: inherit; font-size: 13.5px; font-weight: 700; color: #374151;
            transition: border-color .18s, background .18s, color .18s;
          }
          .sh-page-btn:hover:not(:disabled) { border-color: #013d97; color: #013d97; background: #f0f5ff; }
          .sh-page-btn:disabled { opacity: .4; cursor: not-allowed; }
          .sh-page-btn--num { padding: 0; }
          .sh-page-btn--active { background: #013d97; color: #fff; border-color: #013d97; }
          .sh-page-btn--active:hover { background: #022d75; border-color: #022d75; color: #fff; }
          .sh-page-ellipsis { color: #9ca3af; font-size: 13px; font-weight: 700; padding: 0 2px; }

          /* ── RESPONSIVE ── */
          @media (max-width: 1180px) {
            .sh-grid { grid-template-columns: repeat(3, 1fr); }
          }
          @media (max-width: 1100px) {
            .sh-action-btn__label { display: none; }
            .sh-action-btn { padding: 8px 10px; }
          }
          @media (max-width: 1024px) {
            .sh-body__inner { grid-template-columns: 220px 1fr; }
          }
          @media (max-width: 768px) {
            .sh-hero { min-height: 180px; }
            .sh-hero__arrow { width: 34px; height: 34px; }
            .sh-hero__arrow--prev { left: 10px; }
            .sh-hero__arrow--next { right: 10px; }
            .sh-toolbar { padding: 8px 16px; gap: 8px; }
            .sh-cats { padding: 0 16px 10px; }
            .sh-body { padding: 24px 0 60px; }
            .sh-body__inner { padding: 0 16px; grid-template-columns: 1fr; }
            .sh-sidebar { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 200; background: rgba(0,0,0,.4); display: none; }
            .sh-sidebar--open { display: flex; align-items: flex-end; }
            .sh-sidebar--open .sh-filters { border-radius: 14px 14px 0 0; max-height: 80vh; overflow-y: auto; width: 100%; }
            .sh-filter-toggle { display: flex; }
            .sh-toolbar-wrap { top: 80px; }
            .sh-sort--desktop { display: none; }
            .sh-toolbar-mobile-sort { display: flex; padding: 0 16px 8px; }
            .sh-sort--mobile { display: block; flex: 1; }
            .sh-action-btn__label { display: none; }
            .sh-action-btn { padding: 8px 10px; }
            .sh-grid { grid-template-columns: repeat(2, 1fr); }
          }
          @media (max-width: 480px) {
            .sh-hero { min-height: 140px; }
            .sh-hero__dots { bottom: 10px; }
            .sh-toolbar-wrap { top: 70px; }
            .sh-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
            .sh-card__cta-row { gap: 6px; }
            .sh-card__cta { font-size: 11.5px; padding: 9px 6px; gap: 5px; }
            .sh-card__wa-btn { width: 34px; }
            .sh-page-btn { min-width: 34px; height: 34px; padding: 0 10px; font-size: 12.5px; }
            .sh-page-btn__label { display: none; }
          }

          a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
          @media (prefers-reduced-motion: reduce) {
            .sh *, .sh *::before, .sh *::after { transition-duration: 0ms !important; }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default ShopPage;